import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardBody,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
  useToast,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import { Link as RouterLink, Navigate, useNavigate } from 'react-router-dom';
import { FaPencilAlt, FaPlus, FaTrash } from 'react-icons/fa';
import { useUser } from '../../../contexts/UserContext';
import { getUserRoles } from '../../../utils/authRoles';
import {
  createAdminNetwork,
  deleteAdminNetwork,
  getAdminConsultants,
  getAdminNetworks,
} from './adminNetworksApi';

const emptyForm = { name: '', description: '', consultant_id: '' };

const parseOptionalNumber = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) return Number(value);
  return null;
};

const getOrganizationCount = (network) => parseOptionalNumber(network?.organizations_count);
const getUserCount = (network) => parseOptionalNumber(network?.users_count);
const renderCount = (value) => (value == null ? '—' : value);

const AdminNetworksListPage = () => {
  const { user, isLoading: isUserLoading } = useUser();
  const isHkeAdmin = useMemo(() => getUserRoles(user).includes('hke_admin'), [user]);
  const navigate = useNavigate();
  const toast = useToast();
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeletingId, setIsDeletingId] = useState(null);
  const [networks, setNetworks] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const fetchedRef = useRef(false);

  const modal = useDisclosure();

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [networksPayload, consultantsPayload] = await Promise.all([
        getAdminNetworks(),
        getAdminConsultants(),
      ]);
      setNetworks(Array.isArray(networksPayload) ? networksPayload : []);
      setConsultants(Array.isArray(consultantsPayload) ? consultantsPayload : []);
    } catch (error) {
      console.error('Error loading admin networks:', error);
      toast({ title: 'Could not load networks', status: 'error', duration: 4000, isClosable: true });
      setNetworks([]);
      setConsultants([]);
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (isUserLoading || !isHkeAdmin || fetchedRef.current) return;
    fetchedRef.current = true;
    loadData();
  }, [isHkeAdmin, isUserLoading, loadData]);

  const consultantNameById = useMemo(() => {
    const map = new Map();
    consultants.forEach((consultant) => {
      if (consultant?.id != null && consultant?.name) {
        map.set(Number(consultant.id), consultant.name);
      }
    });
    return map;
  }, [consultants]);

  const getConsultantName = useCallback((network) => {
    if (network?.consultant?.name) return network.consultant.name;

    const consultantId = network?.consultant_id;
    if (consultantId == null || consultantId === '') return '-';

    return consultantNameById.get(Number(consultantId)) || '-';
  }, [consultantNameById]);

  const openNetwork = useCallback((networkId) => {
    navigate(`/app/admin/networks/${networkId}`);
  }, [navigate]);

  const handleDelete = async (networkId) => {
    const confirmed = window.confirm('Delete this network?');
    if (!confirmed) return;

    setIsDeletingId(networkId);
    try {
      await deleteAdminNetwork(networkId);
      toast({ title: 'Network deleted', status: 'success', duration: 3000, isClosable: true });
      setNetworks((prev) => prev.filter((network) => Number(network.id) !== Number(networkId)));
    } catch (error) {
      console.error('Error deleting network:', error);
      toast({ title: 'Could not delete network', status: 'error', duration: 4000, isClosable: true });
    } finally {
      setIsDeletingId(null);
    }
  };

  const sortedNetworks = useMemo(
    () => [...networks].sort((a, b) => (a?.name || '').localeCompare(b?.name || '')),
    [networks]
  );

  const handleCreate = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    try {
      await createAdminNetwork({
        name: form.name.trim(),
        description: form.description.trim() || null,
        consultant_id: form.consultant_id ? Number(form.consultant_id) : null,
      });
      toast({ title: 'Network created', status: 'success', duration: 3000, isClosable: true });
      setForm(emptyForm);
      modal.onClose();
      await loadData();
    } catch (error) {
      console.error('Error creating network:', error);
      toast({ title: 'Could not create network', status: 'error', duration: 4000, isClosable: true });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isUserLoading) {
    return <HStack justify="center" py={12}><Spinner size="lg" /></HStack>;
  }

  if (!isHkeAdmin) {
    return <Navigate to="/app/dashboard" replace />;
  }

  return (
    <Box p={{ base: 4, md: 6 }}>
      <HStack justify="space-between" align={{ base: 'start', md: 'center' }} mb={4} flexWrap="wrap">
        <Box>
          <Heading size="lg">Networks</Heading>
          <Text color="gray.500">Manage all networks across consultants.</Text>
        </Box>
        <Button leftIcon={<FaPlus />} colorScheme="blue" variant="outline" onClick={modal.onOpen}>
          Create network
        </Button>
      </HStack>

      {isLoading ? (
        <HStack justify="center" py={12}><Spinner size="lg" /></HStack>
      ) : sortedNetworks.length === 0 ? (
        <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
          <CardBody><Text>No networks found.</Text></CardBody>
        </Card>
      ) : (
        <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
          <CardBody>
            <TableContainer>
              <Table>
                <Thead>
                  <Tr>
                    <Th>Name</Th>
                    <Th>Consultant</Th>
                    <Th>Organisations</Th>
                    <Th>Users</Th>
                    <Th textAlign="right">Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {sortedNetworks.map((network) => (
                    <Tr
                      key={network.id}
                      cursor="pointer"
                      _hover={{ bg: 'gray.50' }}
                      onClick={() => openNetwork(network.id)}
                    >
                      <Td fontWeight="semibold">{network.name}</Td>
                      <Td>{getConsultantName(network)}</Td>
                      <Td>{renderCount(getOrganizationCount(network))}</Td>
                      <Td>{renderCount(getUserCount(network))}</Td>
                      <Td onClick={(event) => event.stopPropagation()}>
                        <HStack justify="flex-end">
                          <Tooltip label="Edit" hasArrow>
                            <IconButton
                              as={RouterLink}
                              to={`/app/admin/networks/${network.id}`}
                              size="sm"
                              variant="outline"
                              icon={<FaPencilAlt />}
                              aria-label="Edit network"
                              title="Edit network"
                            />
                          </Tooltip>
                          <Tooltip label="Delete" hasArrow>
                            <IconButton
                              icon={<FaTrash />}
                              size="sm"
                              colorScheme="red"
                              variant="outline"
                              aria-label="Delete network"
                              title="Delete network"
                              isLoading={isDeletingId === network.id}
                              onClick={() => handleDelete(network.id)}
                            />
                          </Tooltip>
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </CardBody>
        </Card>
      )}

      <Modal isOpen={modal.isOpen} onClose={modal.onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create network</ModalHeader>
          <ModalCloseButton />
          <form onSubmit={handleCreate}>
            <ModalBody>
              <VStack spacing={3}>
                <FormControl isRequired>
                  <FormLabel>Name</FormLabel>
                  <Input value={form.name} onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))} />
                </FormControl>
                <FormControl>
                  <FormLabel>Description</FormLabel>
                  <Input value={form.description} onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))} />
                </FormControl>
                <FormControl>
                  <FormLabel>Consultant</FormLabel>
                  <Select value={form.consultant_id} onChange={(event) => setForm((prev) => ({ ...prev, consultant_id: event.target.value }))}>
                    <option value="">No consultant</option>
                    {consultants.map((consultant) => (
                      <option key={consultant.id} value={consultant.id}>{consultant.name}</option>
                    ))}
                  </Select>
                </FormControl>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={modal.onClose}>Cancel</Button>
              <Button type="submit" colorScheme="blue" isLoading={isSubmitting}>Create</Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default AdminNetworksListPage;
